// Fix IR (2026-11-01): "También en archivos, podemos agregar un botón de Calendario de reuniones, con formato de
// calendario tipo google calendar, en el cual se puedan agendar reuniones. También que se pueda adjuntar documentos
// tipo acta a cada reunión. Para las actas subidas que se pueda añadir un título y pequeña descripción, también que
// esté una pestaña de actas donde se pueda visualizar y filtrar" (CEN, 30/10).
// Calendario mensual (lunes a domingo): un clic en un día agenda una reunión ese día; un clic en una reunión la abre,
// con sus actas (título, descripción corta y archivo). La pestaña «Actas» lista todas y filtra por texto y por fechas.
// Los permisos los controla el servidor, igual que en Archivos de Empresa.
import { useState, useEffect, useCallback } from "react";
import {
  ChevronLeft, ChevronRight, Plus, X, Loader2, Upload, Download, Trash2, RotateCcw, CalendarDays,
  ClipboardList, FileText, Search, Pencil, Clock, MapPin, Users,
} from "lucide-react";
import axios from "axios";
import { API } from "../../contexts/AuthContext";
import { toast } from "sonner";

const opts = { withCredentials: true };
const DIAS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];
const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
const MAX_DESC = 300;
const pad = (n) => String(n).padStart(2, "0");
const isoDia = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const fechaCorta = (s) => (s && s.length >= 10 ? `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}` : "");
const horario = (r) => (r.hora_inicio ? (r.hora_fin ? `${r.hora_inicio}–${r.hora_fin}` : r.hora_inicio) : "Todo el día");
const error = (e, txt) => toast.error(e?.response?.data?.detail || txt);

// Seis semanas que empiezan el lunes anterior (o igual) al día 1 del mes.
export function diasDelMes(anio, mes) {
  const primero = new Date(anio, mes, 1);
  const desplazamiento = (primero.getDay() + 6) % 7;
  return Array.from({ length: 42 }, (_, i) => new Date(anio, mes, 1 - desplazamiento + i));
}

export function BotonesReuniones({ tab, onChange }) {
  const cls = (activo) => `px-4 py-2 rounded-xl text-sm font-semibold border transition-all flex items-center gap-1.5 ${activo ? "bg-[#7C3AED] text-white border-[#7C3AED]" : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"}`;
  return (
    <>
      <button onClick={() => onChange("Calendario")} className={cls(tab === "Calendario")} data-testid="archivos-empresa-tab-Calendario">
        <CalendarDays size={14} /> Calendario de reuniones
      </button>
      <button onClick={() => onChange("Actas")} className={cls(tab === "Actas")} data-testid="archivos-empresa-tab-Actas">
        <ClipboardList size={14} /> Actas
      </button>
    </>
  );
}

function Modal({ titulo, onClose, children, testid }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid={testid}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
          <h3 className="font-bold text-gray-900">{titulo}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-testid="reunion-modal-cerrar"><X size={18} /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

const inp = "w-full border border-gray-200 rounded-lg px-3 py-2 text-sm";

function ReunionForm({ inicial, onClose, onSaved }) {
  const [f, setF] = useState({
    titulo: inicial.titulo || "", fecha: inicial.fecha || "", hora_inicio: inicial.hora_inicio || "", hora_fin: inicial.hora_fin || "",
    lugar: inicial.lugar || "", participantes: inicial.participantes || "", notas: inicial.notas || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const guardar = async (e) => {
    e.preventDefault();
    if (!f.titulo.trim() || !f.fecha) { toast.error("Completá el título y la fecha"); return; }
    setSaving(true);
    try {
      const { data } = inicial.id
        ? await axios.put(`${API}/reuniones/${inicial.id}`, f, opts)
        : await axios.post(`${API}/reuniones`, f, opts);
      toast.success(inicial.id ? "Reunión actualizada" : "Reunión agendada");
      onSaved(data);
    } catch (err) { error(err, "Error al guardar la reunión"); }
    finally { setSaving(false); }
  };
  return (
    <Modal titulo={inicial.id ? "Editar reunión" : "Nueva reunión"} onClose={onClose} testid="reunion-form">
      <form onSubmit={guardar} className="space-y-3">
        <input autoFocus value={f.titulo} onChange={set("titulo")} placeholder="Título de la reunión" className={inp} data-testid="reunion-titulo" />
        <div className="grid grid-cols-3 gap-2">
          <label className="text-xs text-gray-500">Fecha<input type="date" value={f.fecha} onChange={set("fecha")} className={inp} data-testid="reunion-fecha" /></label>
          <label className="text-xs text-gray-500">Desde<input type="time" value={f.hora_inicio} onChange={set("hora_inicio")} className={inp} data-testid="reunion-hora-inicio" /></label>
          <label className="text-xs text-gray-500">Hasta<input type="time" value={f.hora_fin} onChange={set("hora_fin")} className={inp} data-testid="reunion-hora-fin" /></label>
        </div>
        <input value={f.lugar} onChange={set("lugar")} placeholder="Lugar o enlace" className={inp} data-testid="reunion-lugar" />
        <input value={f.participantes} onChange={set("participantes")} placeholder="Participantes" className={inp} data-testid="reunion-participantes" />
        <textarea value={f.notas} onChange={set("notas")} placeholder="Notas" rows={3} className={inp} data-testid="reunion-notas" />
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" onClick={onClose} className="px-3 py-2 rounded-lg text-sm text-gray-500">Cancelar</button>
          <button type="submit" disabled={saving} className="px-4 py-2 rounded-lg bg-[#7C3AED] text-white text-sm font-semibold flex items-center gap-1.5" data-testid="reunion-guardar">
            {saving && <Loader2 size={14} className="animate-spin" />} Guardar
          </button>
        </div>
      </form>
    </Modal>
  );
}

function ActaUpload({ reunionId, onSubido }) {
  const [titulo, setTitulo] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [archivo, setArchivo] = useState(null);
  const [subiendo, setSubiendo] = useState(false);
  const [clave, setClave] = useState(0);
  const subir = async (e) => {
    e.preventDefault();
    if (!titulo.trim() || !archivo) { toast.error("Indicá el título y elegí el archivo del acta"); return; }
    setSubiendo(true);
    try {
      const fd = new FormData();
      fd.append("titulo", titulo.trim());
      fd.append("descripcion", descripcion.trim());
      fd.append("file", archivo);
      await axios.post(`${API}/reuniones/${reunionId}/actas`, fd, opts);
      toast.success("Acta adjuntada");
      setTitulo(""); setDescripcion(""); setArchivo(null); setClave((k) => k + 1);
      onSubido();
    } catch (err) { error(err, "Error al subir el acta (máx. 12 MB)"); }
    finally { setSubiendo(false); }
  };
  return (
    <form onSubmit={subir} className="space-y-2 bg-gray-50 rounded-xl p-3" data-testid="acta-upload-form">
      <p className="text-xs font-bold text-gray-500 uppercase">Adjuntar acta</p>
      <input value={titulo} onChange={(e) => setTitulo(e.target.value)} placeholder="Título del acta" className={inp} data-testid="acta-titulo" />
      <textarea value={descripcion} onChange={(e) => setDescripcion(e.target.value.slice(0, MAX_DESC))} rows={2}
        placeholder="Descripción corta" className={inp} data-testid="acta-descripcion" />
      <div className="flex items-center gap-2">
        <input key={clave} type="file" onChange={(e) => setArchivo(e.target.files?.[0] || null)} className="text-xs flex-1 min-w-0" data-testid="acta-archivo" />
        <span className="text-[10px] text-gray-400">{descripcion.length}/{MAX_DESC}</span>
        <button type="submit" disabled={subiendo} className="px-3 py-1.5 rounded-lg bg-[#7C3AED] text-white text-xs font-semibold flex items-center gap-1" data-testid="acta-subir">
          {subiendo ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} Subir
        </button>
      </div>
    </form>
  );
}

const verActa = (id) => window.open(`${API}/reuniones/actas/${id}/download`, "_blank");

function ReunionDetalle({ reunionId, onClose, onEditar, onCambio }) {
  const [r, setR] = useState(null);
  const cargar = useCallback(() => {
    axios.get(`${API}/reuniones/${reunionId}`, opts).then(({ data }) => setR(data)).catch((e) => error(e, "Error al abrir la reunión"));
  }, [reunionId]);
  useEffect(cargar, [cargar]);
  const actaAPapelera = async (id) => {
    try { await axios.delete(`${API}/reuniones/actas/${id}`, opts); toast.success("Acta enviada a la papelera"); cargar(); onCambio(); }
    catch (e) { error(e, "Error"); }
  };
  const borrar = async () => {
    try { await axios.delete(`${API}/reuniones/${reunionId}`, opts); toast.success("Reunión enviada a la papelera"); onCambio(); onClose(); }
    catch (e) { error(e, "Error"); }
  };
  return (
    <Modal titulo={r ? r.titulo : "Reunión"} onClose={onClose} testid="reunion-detalle">
      {!r ? <Loader2 size={18} className="animate-spin text-gray-400" /> : (
        <div className="space-y-4">
          <div className="space-y-1 text-sm text-gray-700">
            <p className="flex items-center gap-2" data-testid="reunion-detalle-cuando"><CalendarDays size={14} className="text-gray-400" />{fechaCorta(r.fecha)} · <Clock size={14} className="text-gray-400" />{horario(r)}</p>
            {r.lugar && <p className="flex items-center gap-2"><MapPin size={14} className="text-gray-400" />{r.lugar}</p>}
            {r.participantes && <p className="flex items-center gap-2"><Users size={14} className="text-gray-400" />{r.participantes}</p>}
            {r.notas && <p className="text-gray-500 whitespace-pre-line">{r.notas}</p>}
          </div>
          <div className="flex gap-2">
            <button onClick={() => onEditar(r)} className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold text-gray-600 flex items-center gap-1" data-testid="reunion-editar"><Pencil size={12} /> Editar</button>
            <button onClick={borrar} className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold text-red-500 flex items-center gap-1" data-testid="reunion-borrar"><Trash2 size={12} /> Enviar a la papelera</button>
          </div>
          <div>
            <p className="text-xs font-bold text-gray-500 uppercase mb-2">Actas ({r.actas.length})</p>
            {r.actas.length === 0 ? <p className="text-xs text-gray-400 mb-2">Sin actas todavía</p> : (
              <div className="divide-y divide-gray-50 mb-3">
                {r.actas.map((a) => (
                  <div key={a.id} className="flex items-start justify-between gap-2 py-2" data-testid={`reunion-acta-${a.id}`}>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-800">{a.titulo}</p>
                      {a.descripcion && <p className="text-xs text-gray-500">{a.descripcion}</p>}
                      <p className="text-[11px] text-gray-400">{a.filename} · {(a.size / 1024).toFixed(0)} KB · {a.uploaded_by_name}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <button onClick={() => verActa(a.id)} className="text-gray-400 hover:text-[#7C3AED]" title="Ver"><Download size={15} /></button>
                      <button onClick={() => actaAPapelera(a.id)} className="text-gray-300 hover:text-red-500" title="Papelera"><Trash2 size={15} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <ActaUpload reunionId={reunionId} onSubido={() => { cargar(); onCambio(); }} />
          </div>
        </div>
      )}
    </Modal>
  );
}

function PapeleraReuniones({ onClose, onCambio }) {
  const [lista, setLista] = useState(null);
  const cargar = useCallback(() => {
    axios.get(`${API}/reuniones`, { ...opts, params: { papelera: true } }).then(({ data }) => setLista(data || [])).catch((e) => error(e, "Error cargando la papelera"));
  }, []);
  useEffect(cargar, [cargar]);
  const restaurar = async (id) => {
    try { await axios.post(`${API}/reuniones/${id}/restore`, {}, opts); toast.success("Reunión restaurada"); cargar(); onCambio(); }
    catch (e) { error(e, "Error"); }
  };
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4" data-testid="reuniones-papelera">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-bold text-amber-700 uppercase">Papelera — reuniones</p>
        <button onClick={onClose} className="text-xs text-gray-400 hover:text-gray-600">Cerrar</button>
      </div>
      {!lista ? <Loader2 size={14} className="animate-spin text-gray-400" /> : lista.length === 0 ? <p className="text-xs text-gray-400">Vacía</p> : lista.map((r) => (
        <div key={r.id} className="flex items-center justify-between text-xs py-1">
          <span>{fechaCorta(r.fecha)} · {r.titulo}</span>
          <button onClick={() => restaurar(r.id)} className="text-emerald-600 font-semibold hover:underline" data-testid={`reunion-restore-${r.id}`}>Restaurar</button>
        </div>
      ))}
    </div>
  );
}

export function CalendarioReuniones() {
  const hoy = new Date();
  const [mes, setMes] = useState({ anio: hoy.getFullYear(), mes: hoy.getMonth() });
  const [reuniones, setReuniones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);       // {fecha} para nueva o la reunión a editar
  const [abierta, setAbierta] = useState(null); // id de la reunión abierta
  const [dia, setDia] = useState(null);         // "AAAA-MM-DD" con todas sus reuniones
  const [papelera, setPapelera] = useState(false);
  const dias = diasDelMes(mes.anio, mes.mes);
  const desde = isoDia(dias[0]), hasta = isoDia(dias[41]);

  const cargar = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/reuniones`, { ...opts, params: { desde, hasta } })
      .then(({ data }) => setReuniones(data || []))
      .catch((e) => error(e, "Error cargando las reuniones"))
      .finally(() => setLoading(false));
  }, [desde, hasta]);
  useEffect(cargar, [cargar]);

  const porDia = {};
  reuniones.forEach((r) => { (porDia[r.fecha] = porDia[r.fecha] || []).push(r); });
  const mover = (n) => setMes(({ anio, mes: m }) => { const d = new Date(anio, m + n, 1); return { anio: d.getFullYear(), mes: d.getMonth() }; });
  const hoyIso = isoDia(hoy);
  const chip = (r) => (
    <button key={r.id} onClick={(e) => { e.stopPropagation(); setAbierta(r.id); }}
      className="w-full text-left truncate text-[11px] px-1.5 py-0.5 rounded bg-[#7C3AED]/10 text-[#5B21B6] hover:bg-[#7C3AED]/20" data-testid={`reunion-chip-${r.id}`}>
      <span className="font-semibold">{r.hora_inicio || "•"}</span> {r.titulo}{r.actas_count > 0 ? ` · ${r.actas_count} acta${r.actas_count > 1 ? "s" : ""}` : ""}
    </button>
  );

  return (
    <div className="space-y-3" data-testid="reuniones-calendario">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <button onClick={() => setMes({ anio: hoy.getFullYear(), mes: hoy.getMonth() })} className="px-3 py-1.5 rounded-lg border border-gray-200 text-sm font-semibold text-gray-600" data-testid="calendario-hoy">Hoy</button>
          <button onClick={() => mover(-1)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500" data-testid="calendario-anterior"><ChevronLeft size={18} /></button>
          <button onClick={() => mover(1)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500" data-testid="calendario-siguiente"><ChevronRight size={18} /></button>
          <h2 className="text-lg font-bold text-gray-800" data-testid="calendario-mes">{MESES[mes.mes][0].toUpperCase()}{MESES[mes.mes].slice(1)} de {mes.anio}</h2>
          {loading && <Loader2 size={14} className="animate-spin text-gray-400" />}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setPapelera(true)} className="text-xs text-gray-400 hover:text-gray-600 flex items-center gap-1" data-testid="reuniones-papelera-btn"><RotateCcw size={12} /> Papelera</button>
          <button onClick={() => setForm({ fecha: hoyIso })} className="px-3 py-2 rounded-xl bg-[#7C3AED] text-white text-sm font-semibold flex items-center gap-1.5" data-testid="reunion-nueva">
            <Plus size={14} /> Nueva reunión
          </button>
        </div>
      </div>
      {papelera && <PapeleraReuniones onClose={() => setPapelera(false)} onCambio={cargar} />}
      <div className="bg-white rounded-2xl border border-gray-200/60 overflow-hidden">
        <div className="grid grid-cols-7 bg-gray-50 border-b border-gray-100">
          {DIAS.map((d) => <div key={d} className="px-2 py-1.5 text-[11px] font-bold text-gray-500 uppercase text-center">{d}</div>)}
        </div>
        <div className="grid grid-cols-7">
          {dias.map((d) => {
            const k = isoDia(d), lista = porDia[k] || [], fuera = d.getMonth() !== mes.mes;
            return (
              <div key={k} onClick={() => setForm({ fecha: k })} title="Agendar una reunión este día"
                className={`min-h-[104px] border-b border-r border-gray-100 p-1 cursor-pointer hover:bg-violet-50/40 ${fuera ? "bg-gray-50/60" : ""}`} data-testid={`calendario-dia-${k}`}>
                <div className="flex justify-center mb-1">
                  <span className={`text-xs w-6 h-6 flex items-center justify-center rounded-full ${k === hoyIso ? "bg-[#7C3AED] text-white font-bold" : fuera ? "text-gray-300" : "text-gray-600"}`}>{d.getDate()}</span>
                </div>
                <div className="space-y-0.5">
                  {lista.slice(0, 3).map(chip)}
                  {lista.length > 3 && (
                    <button onClick={(e) => { e.stopPropagation(); setDia(k); }} className="text-[11px] text-gray-500 hover:underline px-1" data-testid={`calendario-mas-${k}`}>+{lista.length - 3} más</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {dia && (
        <Modal titulo={`Reuniones del ${fechaCorta(dia)}`} onClose={() => setDia(null)} testid="calendario-dia-lista">
          <div className="space-y-1">{(porDia[dia] || []).map((r) => (
            <button key={r.id} onClick={() => { setDia(null); setAbierta(r.id); }} className="w-full text-left text-sm px-2 py-1.5 rounded-lg hover:bg-gray-50">
              <span className="font-semibold text-[#5B21B6]">{horario(r)}</span> · {r.titulo}
            </button>
          ))}</div>
        </Modal>
      )}
      {form && <ReunionForm inicial={form} onClose={() => setForm(null)} onSaved={() => { setForm(null); cargar(); }} />}
      {abierta && <ReunionDetalle reunionId={abierta} onClose={() => setAbierta(null)} onCambio={cargar}
        onEditar={(r) => { setAbierta(null); setForm(r); }} />}
    </div>
  );
}

export function ActasReuniones() {
  const [actas, setActas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [texto, setTexto] = useState("");
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [filtros, setFiltros] = useState({});
  const [papelera, setPapelera] = useState(false);

  const cargar = useCallback(() => {
    setLoading(true);
    const params = { ...filtros };
    if (papelera) params.papelera = true;
    axios.get(`${API}/reuniones/actas`, { ...opts, params })
      .then(({ data }) => setActas(data || []))
      .catch((e) => error(e, "Error cargando las actas"))
      .finally(() => setLoading(false));
  }, [filtros, papelera]);
  useEffect(cargar, [cargar]);

  const filtrar = (e) => {
    e.preventDefault();
    const p = {};
    if (texto.trim()) p.q = texto.trim();
    if (desde) p.desde = desde;
    if (hasta) p.hasta = hasta;
    setFiltros(p);
  };
  const limpiar = () => { setTexto(""); setDesde(""); setHasta(""); setFiltros({}); };
  const accion = async (a) => {
    try {
      if (papelera) { await axios.post(`${API}/reuniones/actas/${a.id}/restore`, {}, opts); toast.success("Acta restaurada"); }
      else { await axios.delete(`${API}/reuniones/actas/${a.id}`, opts); toast.success("Acta enviada a la papelera"); }
      cargar();
    } catch (e) { error(e, "Error"); }
  };

  return (
    <div className="space-y-3" data-testid="actas-reuniones">
      <form onSubmit={filtrar} className="bg-white rounded-2xl border border-gray-200/60 p-3 flex items-end gap-2 flex-wrap">
        <label className="text-xs text-gray-500 flex-1 min-w-[200px]">Buscar (título, descripción, archivo o reunión)
          <input value={texto} onChange={(e) => setTexto(e.target.value)} className={inp} data-testid="actas-filtro-texto" />
        </label>
        <label className="text-xs text-gray-500">Reunión desde<input type="date" value={desde} onChange={(e) => setDesde(e.target.value)} className={inp} data-testid="actas-filtro-desde" /></label>
        <label className="text-xs text-gray-500">hasta<input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className={inp} data-testid="actas-filtro-hasta" /></label>
        <button type="submit" className="px-3 py-2 rounded-lg bg-[#7C3AED] text-white text-sm font-semibold flex items-center gap-1" data-testid="actas-filtrar"><Search size={14} /> Filtrar</button>
        <button type="button" onClick={limpiar} className="px-3 py-2 rounded-lg text-sm text-gray-500" data-testid="actas-limpiar">Limpiar</button>
        <button type="button" onClick={() => setPapelera(!papelera)} className={`px-3 py-2 rounded-lg text-xs flex items-center gap-1 ${papelera ? "bg-amber-100 text-amber-700" : "text-gray-400"}`} data-testid="actas-papelera-btn">
          <RotateCcw size={12} /> {papelera ? "Ver actas" : "Papelera"}
        </button>
      </form>
      <div className="bg-white rounded-2xl border border-gray-200/60 overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead className="bg-gray-50 text-left text-[11px] uppercase text-gray-500">
            <tr><th className="px-3 py-2">Reunión</th><th className="px-3 py-2">Acta</th><th className="px-3 py-2">Archivo</th><th className="px-3 py-2">Subida</th><th className="px-3 py-2"></th></tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="px-3 py-6 text-center"><Loader2 size={16} className="animate-spin inline text-gray-400" /></td></tr>
            ) : actas.length === 0 ? (
              <tr><td colSpan={5} className="px-3 py-6 text-center text-gray-400" data-testid="actas-vacio">{papelera ? "La papelera está vacía" : "No hay actas con esos filtros"}</td></tr>
            ) : actas.map((a) => (
              <tr key={a.id} className="border-t border-gray-50 align-top" data-testid={`acta-fila-${a.id}`}>
                <td className="px-3 py-2 whitespace-nowrap"><p className="font-semibold text-gray-700">{fechaCorta(a.reunion_fecha)}</p><p className="text-xs text-gray-500">{a.reunion_titulo}</p></td>
                <td className="px-3 py-2"><p className="font-semibold text-gray-800">{a.titulo}</p>{a.descripcion && <p className="text-xs text-gray-500">{a.descripcion}</p>}</td>
                <td className="px-3 py-2 text-xs text-gray-500"><span className="flex items-center gap-1"><FileText size={13} className="text-gray-400" />{a.filename}</span>{(a.size / 1024).toFixed(0)} KB</td>
                <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">{fechaCorta(a.uploaded_at)}<br />{a.uploaded_by_name}</td>
                <td className="px-3 py-2 whitespace-nowrap text-right">
                  {!papelera && <button onClick={() => verActa(a.id)} className="text-gray-400 hover:text-[#7C3AED] mr-3" title="Ver" data-testid={`acta-ver-${a.id}`}><Download size={15} /></button>}
                  <button onClick={() => accion(a)} className={papelera ? "text-emerald-600 text-xs font-semibold hover:underline" : "text-gray-300 hover:text-red-500"} title={papelera ? "Restaurar" : "Papelera"} data-testid={`acta-accion-${a.id}`}>
                    {papelera ? "Restaurar" : <Trash2 size={15} />}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-gray-400" data-testid="actas-total">{actas.length} acta{actas.length === 1 ? "" : "s"}</p>
    </div>
  );
}
